import styled, { keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const slideUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(28px) scale(0.98);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const expandToggles = keyframes`
	from {
		opacity: 0;
		transform: translateY(-6px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

export const CookieConsentBanner = styled.div`
	position: fixed;
	bottom: 20px;
	right: 20px;
	left: auto;
	transform: none;
	z-index: 8500;
	box-sizing: border-box;
	width: min(420px, calc(100vw - 40px));
	max-width: calc(100vw - 40px);
	max-height: calc(100dvh - 40px);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 16px;
	background:
		radial-gradient(
			ellipse at 80% -20%,
			rgba(176, 38, 255, 0.16) 0%,
			transparent 50%
		),
		radial-gradient(
			ellipse at 10% 120%,
			rgba(255, 77, 242, 0.07) 0%,
			transparent 45%
		),
		rgba(10, 4, 14, 0.97);
	backdrop-filter: blur(28px) saturate(1.15);
	box-shadow:
		0 2px 0 rgba(255, 255, 255, 0.055) inset,
		0 24px 72px rgba(0, 0, 0, 0.7),
		0 0 0 1px rgba(176, 38, 255, 0.1),
		0 0 32px rgba(176, 38, 255, 0.07);
	animation: ${slideUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
	overflow-x: hidden;
	overflow-y: auto;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		max-width: 100%;
		max-height: min(85dvh, calc(100dvh - env(safe-area-inset-bottom, 0px)));
		border-radius: 14px 14px 0 0;
		padding-bottom: env(safe-area-inset-bottom, 0px);
	}
`;

export const CookieConsentBody = styled.div`
	padding: 14px 16px 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 12px 12px 0;
	}
`;

export const CookieConsentTitle = styled.h2`
	margin: 0 0 4px;
	font-size: 13px;
	font-weight: 700;
	color: rgba(255, 255, 255, 0.92);
	letter-spacing: 0.01em;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin: 0 0 4px;
		font-size: 12px;
	}
`;

export const CookieConsentDescription = styled.p`
	margin: 0;
	font-size: 12px;
	line-height: 1.45;
	color: rgba(255, 255, 255, 0.48);

	a {
		color: rgba(176, 38, 255, 0.82);
		text-decoration: none;
		font-weight: 500;

		&:hover {
			color: rgba(255, 77, 242, 0.9);
			text-decoration: underline;
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 11px;
		line-height: 1.4;
	}
`;

export const CookieConsentToggles = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 10px 16px 0;
	animation: ${expandToggles} 0.28s cubic-bezier(0.22, 1, 0.36, 1);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 5px;
		padding: 8px 12px 0;
	}
`;

export const CookieConsentToggleRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 12px;
	border-radius: 11px;
	border: 1px solid
		${({ $checked }) =>
			$checked ? 'rgba(176, 38, 255, 0.32)' : 'rgba(255, 255, 255, 0.07)'};
	background: ${({ $checked }) =>
		$checked ? 'rgba(176, 38, 255, 0.08)' : 'rgba(255, 255, 255, 0.025)'};
	cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
	user-select: none;
	transition:
		border-color 0.2s,
		background 0.2s;

	&:hover {
		border-color: ${({ $locked, $checked }) =>
			$locked
				? 'rgba(255, 255, 255, 0.07)'
				: $checked
					? 'rgba(176, 38, 255, 0.48)'
					: 'rgba(255, 255, 255, 0.13)'};
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 10px;
		padding: 8px 10px;
		border-radius: 8px;
	}
`;

export const CookieConsentToggleContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
	flex: 1;
`;

export const CookieConsentToggleLabel = styled.span`
	font-size: 11.5px;
	font-weight: 600;
	color: ${({ $checked }) =>
		$checked ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.45)'};
	letter-spacing: 0.01em;
	transition: color 0.2s;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 10px;
	}
`;

export const CookieConsentToggleDescription = styled.span`
	font-size: 10.5px;
	line-height: 1.35;
	font-weight: 400;
	color: rgba(255, 255, 255, 0.38);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 9.5px;
		line-height: 1.3;
	}
`;

export const CookieConsentToggleSwitch = styled.div`
	position: relative;
	width: 32px;
	height: 18px;
	margin-top: 1px;
	border-radius: 20px;
	border: 1.5px solid
		${({ $checked }) => ($checked ? 'transparent' : 'rgba(255,255,255,0.16)')};
	background: ${({ $checked }) =>
		$checked
			? 'linear-gradient(135deg, #b026ff, #ff4df2)'
			: 'rgba(255,255,255,0.06)'};
	flex-shrink: 0;
	transition:
		background 0.22s,
		border-color 0.22s;

	&::after {
		content: '';
		position: absolute;
		top: 2px;
		left: ${({ $checked }) => ($checked ? '14px' : '2px')};
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: ${({ $checked }) =>
			$checked ? '#fff' : 'rgba(255,255,255,0.3)'};
		transition:
			left 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
			background 0.22s;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 28px;
		height: 16px;

		&::after {
			top: 1.5px;
			left: ${({ $checked }) => ($checked ? '12px' : '1.5px')};
			width: 11px;
			height: 11px;
		}
	}
`;

export const CookieConsentActions = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 6px;
	padding: 12px 16px 14px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 5px;
		padding: 10px 12px 12px;
	}
`;

const baseBtn = `
	box-sizing: border-box;
	min-width: 0;
	width: 100%;
	padding: 8px 6px;
	border-radius: 8px;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.01em;
	cursor: pointer;
	transition: opacity 0.18s, transform 0.14s, box-shadow 0.18s, background 0.18s;
	white-space: normal;
	line-height: 1.2;
	text-align: center;

	&:active {
		transform: scale(0.97);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 8px 4px;
		border-radius: 8px;
		font-size: 11px;
		letter-spacing: 0;
	}
`;

export const CookieConsentRejectButton = styled.button`
	${baseBtn}
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.04);
	color: rgba(255, 255, 255, 0.5);

	&:hover {
		border-color: rgba(255, 255, 255, 0.18);
		background: rgba(255, 255, 255, 0.07);
		color: rgba(255, 255, 255, 0.7);
	}
`;

export const CookieConsentCustomizeButton = styled.button`
	${baseBtn}
	border: 1px solid rgba(176, 38, 255, 0.28);
	background: rgba(176, 38, 255, 0.07);
	color: rgba(200, 120, 255, 0.9);

	&:hover {
		border-color: rgba(176, 38, 255, 0.5);
		background: rgba(176, 38, 255, 0.13);
		color: rgba(220, 150, 255, 1);
	}
`;

export const CookieConsentAcceptAllButton = styled.button`
	${baseBtn}
	border: none;
	background: linear-gradient(135deg, #b026ff 0%, #ff4df2 58%, #ffb26c 100%);
	color: #fff;
	box-shadow: 0 4px 16px rgba(176, 38, 255, 0.3);

	&:hover {
		opacity: 0.9;
		box-shadow: 0 6px 22px rgba(176, 38, 255, 0.48);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		box-shadow: 0 2px 10px rgba(176, 38, 255, 0.28);
	}
`;
